import { formatInTimeZone } from 'date-fns-tz';

/** API may send revenue as a number or as a "$12.34" string — normalise both. */
export const parseRevenue = (value) => parseFloat(String(value ?? '0').replace('$', '')) || 0;

const toNumber = (value) => Number(value) || 0;

const numberFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const currencyFmt = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const compactFmt = new Intl.NumberFormat(undefined, {
  notation: 'compact',
  maximumFractionDigits: 1,
});

export const formatCount = (value) => numberFmt.format(value ?? 0);
export const formatMoney = (value) => currencyFmt.format(value ?? 0);
export const formatCompact = (value) => compactFmt.format(value ?? 0);
export const formatPercent = (value, digits = 2) => `${(value ?? 0).toFixed(digits)}%`;

/**
 * Bucket key/label formats per interval. Keys are chosen so that plain
 * lexicographic sorting is also chronological sorting.
 */
const BUCKET_FORMATS = {
  hour: { key: "yyyy-MM-dd'T'HH", label: 'dd.MM HH:mm' },
  day: { key: 'yyyy-MM-dd', label: 'dd.MM' },
  month: { key: 'yyyy-MM', label: 'MMM yyyy' },
};

const bucketFormatsFor = (interval) => BUCKET_FORMATS[interval] ?? BUCKET_FORMATS.day;

export const dateFormatter = (params, selectedTimezone, selectedInterval) => {
  if (!params.value) return '';

  const fmt =
    selectedInterval === 'day'
      ? 'dd.MM.yyyy'
      : selectedInterval === 'month'
        ? 'MMM yyyy'
        : 'HH:mm / dd.MM.yyyy';
  return formatInTimeZone(new Date(params.value), selectedTimezone, fmt);
};

export const currencyFormatter = (params) => formatMoney(Number(params.value) || 0);

export const percentageFormatter = (params) => formatPercent(Number(params.value) || 0, 1);

export const numberFormatter = (params) => formatCount(Number(params.value) || 0);

export const getColumnDefs = (isSmallScreen, selectedTimezone, selectedInterval) => {
  return [
    {
      field: 'project',
      headerName: 'Project',
      width: 120,
      flex: 1,
      pinned: isSmallScreen ? null : 'left',
    },
    { field: 'campaign_id', headerName: 'Campaign ID', width: 120 },
    {
      field: 'clicks',
      headerName: 'Clicks',
      filter: 'agNumberColumnFilter',
      width: 90,
      valueFormatter: numberFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'installs',
      headerName: 'Installs',
      filter: 'agNumberColumnFilter',
      width: 90,
      valueFormatter: numberFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'trial',
      headerName: 'Trial',
      filter: 'agNumberColumnFilter',
      hide: isSmallScreen,
      width: 90,
      valueFormatter: numberFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'purchases',
      headerName: 'Purch',
      filter: 'agNumberColumnFilter',
      width: 90,
      valueFormatter: numberFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'revenue',
      headerName: 'Revenue',
      filter: 'agNumberColumnFilter',
      width: 100,
      valueFormatter: currencyFormatter,
      cellStyle: {
        textAlign: 'right',
        fontWeight: '600',
        color: 'var(--color-success)',
      },
    },
    {
      field: 'install_cr',
      headerName: 'Inst CR',
      filter: 'agNumberColumnFilter',
      hide: isSmallScreen,
      width: 90,
      valueFormatter: percentageFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'purchase_cr',
      headerName: 'Purch CR',
      filter: 'agNumberColumnFilter',
      hide: isSmallScreen,
      width: 100,
      valueFormatter: percentageFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'rpc',
      headerName: 'RPC',
      filter: 'agNumberColumnFilter',
      hide: isSmallScreen,
      width: 90,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'arpu',
      headerName: 'ARPU',
      filter: 'agNumberColumnFilter',
      hide: isSmallScreen,
      width: 90,
      valueFormatter: currencyFormatter,
      cellStyle: { textAlign: 'right' },
    },
    {
      field: 'date',
      headerName: 'Date',
      // Value is an ISO string already shifted into the selected timezone, so
      // lexicographic sorting is also chronological. Keep the default text
      // filter — agDateColumnFilter would need Date objects.
      valueFormatter: (params) => dateFormatter(params, selectedTimezone, selectedInterval),
      width: 150,
      sort: 'desc',
    },
  ];
};

/** Stable identity for a stat row — used as ag-grid's getRowId. */
export const getStatRowId = (row) => `${row.project}|${row.campaign_id}|${row.date}`;

export const transformStatData = (stat, excludeNoInstalls, selectedTimezone) => {
  if (!Array.isArray(stat)) return [];

  return stat
    .filter((acc) => !excludeNoInstalls || toNumber(acc.installs) > 0)
    .map((acc) => {
      const clicks = toNumber(acc.clicks);
      const installs = toNumber(acc.installs);
      const purchases = toNumber(acc.purchases);
      const revenue = parseRevenue(acc.revenue);

      return {
        project: acc.project,
        campaign_id: acc.campaignid,
        clicks,
        installs,
        purchases,
        trial: toNumber(acc.trial),
        revenue,
        install_cr: clicks ? (installs / clicks) * 100 : 0,
        purchase_cr: installs ? (purchases / installs) * 100 : 0,
        rpc: clicks ? revenue / clicks : 0,
        arpu: installs ? revenue / installs : 0,
        date: acc.date
          ? formatInTimeZone(new Date(acc.date), selectedTimezone, "yyyy-MM-dd'T'HH:mm:ssXXX")
          : '',
      };
    });
};

const emptyBucket = () => ({ clicks: 0, installs: 0, purchases: 0, trial: 0, revenue: 0 });

const addRow = (target, item) => {
  target.clicks += toNumber(item.clicks);
  target.installs += toNumber(item.installs);
  target.purchases += toNumber(item.purchases);
  target.trial += toNumber(item.trial);
  target.revenue += parseRevenue(item.revenue);
};

/**
 * Group rows into time buckets **in the selected timezone and at the selected
 * interval**, so charts line up with what the grid shows. Bucketing in UTC
 * while the grid renders in another timezone puts the same row on different
 * days in the two views.
 */
export const aggregateByBucket = (stat, selectedTimezone, selectedInterval) => {
  if (!Array.isArray(stat) || stat.length === 0) return [];

  const { key: keyFmt, label: labelFmt } = bucketFormatsFor(selectedInterval);
  const buckets = new Map();

  for (const item of stat) {
    if (!item.date) continue;
    const instant = new Date(item.date);
    if (Number.isNaN(instant.getTime())) continue;

    const key = formatInTimeZone(instant, selectedTimezone, keyFmt);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: formatInTimeZone(instant, selectedTimezone, labelFmt),
        ...emptyBucket(),
      };
      buckets.set(key, bucket);
    }
    addRow(bucket, item);
  }

  return [...buckets.values()].sort((a, b) => a.key.localeCompare(b.key));
};

export const aggregateByProject = (stat) => {
  if (!Array.isArray(stat) || stat.length === 0) return [];

  const projects = new Map();
  for (const item of stat) {
    if (!item.project) continue;
    let entry = projects.get(item.project);
    if (!entry) {
      entry = { name: item.project, ...emptyBucket() };
      projects.set(item.project, entry);
    }
    addRow(entry, item);
  }

  return [...projects.values()].sort((a, b) => b.revenue - a.revenue);
};

export const computeTotals = (stat) => {
  const totals = emptyBucket();
  if (Array.isArray(stat)) {
    for (const item of stat) addRow(totals, item);
  }

  return {
    ...totals,
    installCr: totals.clicks > 0 ? (totals.installs / totals.clicks) * 100 : 0,
    purchaseCr: totals.installs > 0 ? (totals.purchases / totals.installs) * 100 : 0,
  };
};

/**
 * Fractional change between two totals. Returns null when there is no
 * meaningful baseline, so callers can hide the delta instead of showing an
 * infinite or misleading percentage.
 */
export const relativeChange = (current, previous) => {
  if (typeof previous !== 'number' || !Number.isFinite(previous) || previous === 0) return null;
  return (current - previous) / previous;
};
